import { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Package, Copy } from 'lucide-react';
import axios from 'axios';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminPage() {
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [newOrdersCount, setNewOrdersCount] = useState(0);
  const [showProductModal, setShowProductModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [settings, setSettings] = useState({
    paypal_client_id: '',
    paypal_secret: '',
    iban: '',
    iban_holder: '',
    iban_bank: '',
    resend_api_key: '',
    sender_email: '',
    email_configured: false,
    delivery_enabled: false,
    delivery_day: '',
    homepage_title: '',
    homepage_subtitle: '',
    homepage_description: '',
    homepage_order_text: '',
    homepage_button_text: '',
    homepage_categories_title: '',
    homepage_features_title: '',
    homepage_feature1_title: '',
    homepage_feature1_desc: '',
    homepage_feature2_title: '',
    homepage_feature2_desc: '',
    homepage_feature3_title: '',
    homepage_feature3_desc: '',
    footer_text: '',
    footer_subtext: ''
  });
  const [productForm, setProductForm] = useState({
    name: '',
    description: '',
    category: 'petshop',
    subcategory: '',
    price: '',
    stock: '',
    image_url: '',
    images: [],
    availability_date: '',
    available_on_order: false
  });
  const [newImageUrl, setNewImageUrl] = useState('');
  const { token, user } = useAuth();
  const navigate = useNavigate();

  // Definizione sottocategorie Pet Shop
  const PETSHOP_SUBCATEGORIES = [
    { value: 'cane', label: 'Cane', children: ['Cibo Umido', 'Cibo Secco'] },
    { value: 'gatto', label: 'Gatto', children: ['Cibo Secco', 'Cibo Umido'] }
  ];

  const getSubcategoryOptions = () => {
    const options = [];
    PETSHOP_SUBCATEGORIES.forEach(parent => {
      options.push({ value: parent.value, label: parent.label, isParent: true });
      parent.children.forEach(child => {
        options.push({ 
          value: `${parent.value}_${child.toLowerCase().replace(' ', '_')}`, 
          label: `${parent.label} - ${child}`,
          isParent: false 
        });
      });
    });
    return options;
  };

  useEffect(() => {
    if (!user?.is_admin) {
      navigate('/');
      return;
    }
    fetchData();
  }, [user]);

  const fetchData = async () => {
    try {
      const [productsRes, ordersRes, settingsRes, newOrdersRes] = await Promise.all([
        axios.get(`${API}/products`),
        axios.get(`${API}/admin/orders`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/settings`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/orders/new/count`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setProducts(productsRes.data);
      setOrders(ordersRes.data);
      setSettings(settingsRes.data);
      setNewOrdersCount(newOrdersRes.data.count);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    }
  };

  const handleProductSubmit = async (e) => {
    e.preventDefault();
    
    if (uploading) {
      toast.warning('Attendi il caricamento dell\'immagine');
      return;
    }
    
    try {
      const data = {
        ...productForm,
        price: parseFloat(productForm.price),
        stock: parseInt(productForm.stock)
      };

      if (editingProduct) {
        await axios.put(
          `${API}/products/${editingProduct.id}`,
          data,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Prodotto aggiornato');
      } else {
        await axios.post(
          `${API}/products`,
          data,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Prodotto creato');
      }

      setShowProductModal(false);
      setEditingProduct(null);
      setProductForm({ name: '', description: '', category: 'petshop', subcategory: '', price: '', stock: '', image_url: '', images: [], availability_date: '', available_on_order: false });
      setNewImageUrl('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) {
      console.log('No file selected');
      return;
    }

    // Validazione tipo file
    if (!file.type.startsWith('image/')) {
      toast.error('Solo immagini sono permesse');
      e.target.value = '';
      return;
    }

    // Validazione dimensione (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('L\'immagine è troppo grande (max 5MB)');
      e.target.value = '';
      return;
    }

    console.log('Starting upload:', file.name, file.type, file.size);

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', file);

      console.log('Uploading to:', `${API}/upload`);
      const response = await axios.post(`${API}/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`
        },
        timeout: 30000
      });

      console.log('Upload response:', response.data);
      
      if (!response.data || !response.data.url) {
        throw new Error('Risposta upload non valida');
      }

      const imageUrl = `${process.env.REACT_APP_BACKEND_URL}${response.data.url}`;
      console.log('Setting image URL:', imageUrl);
      
      setProductForm(prev => {
        const updated = { ...prev, image_url: imageUrl };
        console.log('Updated form:', updated);
        return updated;
      });
      
      toast.success('Immagine caricata con successo');
    } catch (error) {
      console.error('Upload error details:', error);
      console.error('Error response:', error.response?.data);
      toast.error(error.response?.data?.detail || error.message || 'Errore durante il caricamento dell\'immagine');
    } finally {
      setUploading(false);
      if (e.target) {
        e.target.value = '';
      }
    }
  };

  const handleDeleteProduct = async (id) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo prodotto?')) return;

    try {
      await axios.delete(`${API}/products/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Prodotto eliminato');
      fetchData();
    } catch (error) {
      toast.error('Errore durante l\'eliminazione');
    }
  };

  const handleDuplicateProduct = async (product) => {
    try {
      const duplicatedProduct = {
        name: `${product.name} (Copia)`,
        description: product.description,
        category: product.category,
        price: product.price,
        stock: product.stock,
        image_url: product.image_url || '',
        images: product.images || [],
        availability_date: product.availability_date || '',
        available_on_order: product.available_on_order || false
      };

      await axios.post(`${API}/products`, duplicatedProduct, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Prodotto duplicato con successo!');
      fetchData();
    } catch (error) {
      toast.error('Errore durante la duplicazione');
    }
  };

  const handleEditProduct = (product) => {
    setEditingProduct(product);
    setProductForm({
      name: product.name,
      description: product.description,
      category: product.category,
      subcategory: product.subcategory || '',
      price: product.price.toString(),
      stock: product.stock.toString(),
      image_url: product.image_url || '',
      images: product.images || [],
      availability_date: product.availability_date || '',
      available_on_order: product.available_on_order || false
    });
    setNewImageUrl('');
    setShowProductModal(true);
  };

  const handleUpdateOrderStatus = async (orderId, newStatus) => {
    try {
      await axios.patch(
        `${API}/admin/orders/${orderId}/status`,
        { order_status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Stato ordine aggiornato');
      fetchData();
    } catch (error) {
      toast.error('Errore durante l\'aggiornamento');
    }
  };

  const handleDeleteOrder = async (orderId, orderStatus) => {
    // Permetti eliminazione solo per ordini completati/evasi
    if (orderStatus !== 'completato' && orderStatus !== 'evaso') {
      toast.error('Puoi eliminare solo ordini già evasi');
      return;
    }
    
    if (!window.confirm('Sei sicuro di voler eliminare questo ordine? L\'azione è irreversibile.')) {
      return;
    }

    try {
      await axios.delete(
        `${API}/admin/orders/${orderId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Ordine eliminato');
      fetchData();
    } catch (error) {
      toast.error('Errore durante l\'eliminazione');
    }
  };

  const handleSaveSettings = async (e) => {
    e.preventDefault();
    try {
      await axios.put(
        `${API}/admin/settings`,
        settings,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Impostazioni salvate con successo');
      fetchData();
    } catch (error) {
      toast.error('Errore durante il salvataggio');
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <div className="container mx-auto px-4 md:px-6 max-w-7xl py-8">
        <h1 className="text-4xl font-extrabold mb-8" data-testid="admin-title">Pannello Admin</h1>

        <Tabs defaultValue="products" className="w-full">
          <TabsList className="grid w-full max-w-xl grid-cols-3">
            <TabsTrigger value="products" data-testid="products-tab">Prodotti</TabsTrigger>
            <TabsTrigger value="orders" data-testid="orders-tab" className="relative">
              Ordini
              {newOrdersCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-rose-600 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-bold">
                  {newOrdersCount}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="settings" data-testid="settings-tab">Impostazioni</TabsTrigger>
          </TabsList>

          <TabsContent value="products" className="mt-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold">Gestione Prodotti</h2>
              <Button
                onClick={() => {
                  setEditingProduct(null);
                  setProductForm({ name: '', description: '', category: 'petshop', subcategory: '', price: '', stock: '', image_url: '', images: [], availability_date: '', available_on_order: false });
                  setNewImageUrl('');
                  setShowProductModal(true);
                }}
                className="bg-rose-600 hover:bg-rose-700 text-white rounded-full"
                data-testid="add-product-button"
              >
                <Plus className="mr-2 h-4 w-4" />
                Aggiungi Prodotto
              </Button>
            </div>

            <div className="grid gap-4">
              {products.map((product) => (
                <div
                  key={product.id}
                  className="flex items-center gap-4 p-4 bg-white border border-gray-200 rounded-xl"
                  data-testid={`admin-product-${product.id}`}
                >
                  <div className="w-20 h-20 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                    {product.image_url ? (
                      <img src={product.image_url} alt={product.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Package className="h-8 w-8 text-gray-400" />
                      </div>
                    )}
                  </div>

                  <div className="flex-1">
                    <h3 className="font-bold text-lg">{product.name}</h3>
                    <p className="text-sm text-gray-600 line-clamp-1">{product.description}</p>
                    <div className="flex gap-4 mt-1">
                      <span className="text-sm">
                        <span className="font-semibold">Prezzo:</span> €{product.price.toFixed(2)}
                      </span>
                      <span className="text-sm">
                        <span className="font-semibold">Stock:</span> {product.stock}
                      </span>
                      <span className="text-sm">
                        <span className="font-semibold">Categoria:</span> {product.category}
                      </span>
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => handleEditProduct(product)}
                      title="Modifica prodotto"
                      data-testid={`edit-product-${product.id}`}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => handleDuplicateProduct(product)}
                      title="Duplica prodotto"
                      className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                      data-testid={`duplicate-product-${product.id}`}
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => handleDeleteProduct(product.id)}
                      title="Elimina prodotto"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      data-testid={`delete-product-${product.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="orders" className="mt-6">
            <h2 className="text-2xl font-bold mb-6">Gestione Ordini</h2>

            {orders.length === 0 ? (
              <p className="text-center text-gray-500 py-8" data-testid="no-admin-orders">Nessun ordine</p>
            ) : (
              <div className="space-y-4">
                {orders.map((order) => (
                  <div
                    key={order.id}
                    className="bg-white border border-gray-200 rounded-2xl p-6"
                    data-testid={`admin-order-${order.id}`}
                  >
                    <div className="flex flex-col md:flex-row md:items-start md:justify-between mb-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="font-bold text-lg">Ordine #{order.id.slice(0, 8)}</span>
                        </div>
                        <p className="text-sm text-gray-600 mb-1">
                          <span className="font-semibold">Cliente:</span> {order.user_name} ({order.user_email})
                        </p>
                        <p className="text-sm text-gray-600 mb-1">
                          <span className="font-semibold">Data:</span>{' '}
                          {format(new Date(order.created_at), 'dd MMMM yyyy, HH:mm', { locale: it })}
                        </p>
                        <p className="text-sm text-gray-600">
                          <span className="font-semibold">Consegna:</span>{' '}
                          {order.delivery_type === 'pickup' ? 'Ritiro in negozio' : 'Consegna a domicilio'}
                        </p>
                      </div>

                      <div className="mt-4 md:mt-0 flex flex-col items-end gap-2">
                        <p className="text-2xl font-bold text-emerald-600">€{order.total.toFixed(2)}</p>
                        <div className="flex items-center gap-2">
                          <Select
                            value={order.order_status}
                            onValueChange={(value) => handleUpdateOrderStatus(order.id, value)}
                          >
                            <SelectTrigger className="w-40" data-testid={`status-select-${order.id}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="inviato">📥 Inviato</SelectItem>
                              <SelectItem value="in preparazione">⏳ In Preparazione</SelectItem>
                              <SelectItem value="pronto">✅ Pronto</SelectItem>
                              <SelectItem value="evaso">📦 Evaso</SelectItem>
                            </SelectContent>
                          </Select>
                          
                          {/* Pulsante Elimina - solo per ordini evasi */}
                          {order.order_status === 'evaso' && (
                            <Button
                              variant="outline"
                              size="icon"
                              onClick={() => handleDeleteOrder(order.id, order.order_status)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              title="Elimina ordine evaso"
                              data-testid={`delete-order-${order.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="pt-4 border-t">
                      <h4 className="font-semibold text-sm mb-2">Prodotti:</h4>
                      <div className="space-y-1">
                        {order.items.map((item, idx) => (
                          <div key={idx} className="flex justify-between text-sm">
                            <span>
                              {item.name} x {item.quantity}
                            </span>
                            <span>€{(item.price * item.quantity).toFixed(2)}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {order.notes && (
                      <div className="pt-4 border-t mt-4">
                        <p className="text-sm text-gray-600">
                          <span className="font-semibold">Note:</span> {order.notes}
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="settings" className="mt-6">
            <h2 className="text-2xl font-bold mb-6">Impostazioni Pagamenti</h2>

            <form onSubmit={handleSaveSettings} className="space-y-8 max-w-2xl">
              <div className="bg-white p-6 rounded-2xl border border-gray-200">
                <h3 className="text-xl font-bold mb-4 text-rose-600">Configurazione PayPal</h3>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="paypal_client_id">PayPal Client ID</Label>
                    <Input
                      id="paypal_client_id"
                      value={settings.paypal_client_id || ''}
                      onChange={(e) => setSettings({ ...settings, paypal_client_id: e.target.value })}
                      placeholder="Inserisci il Client ID di PayPal"
                      data-testid="paypal-client-id-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="paypal_secret">PayPal Secret</Label>
                    <Input
                      id="paypal_secret"
                      type="password"
                      value={settings.paypal_secret || ''}
                      onChange={(e) => setSettings({ ...settings, paypal_secret: e.target.value })}
                      placeholder="Inserisci il Secret di PayPal"
                      data-testid="paypal-secret-input"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Lascia vuoto per non modificare il secret esistente
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-2xl border border-gray-200">
                <h3 className="text-xl font-bold mb-4 text-emerald-600">Configurazione IBAN</h3>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="iban">IBAN</Label>
                    <Input
                      id="iban"
                      value={settings.iban || ''}
                      onChange={(e) => setSettings({ ...settings, iban: e.target.value })}
                      placeholder="IT00 X000 0000 0000 0000 0000 000"
                      data-testid="iban-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="iban_holder">Intestatario</Label>
                    <Input
                      id="iban_holder"
                      value={settings.iban_holder || ''}
                      onChange={(e) => setSettings({ ...settings, iban_holder: e.target.value })}
                      placeholder="Nome e Cognome o Ragione Sociale"
                      data-testid="iban-holder-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="iban_bank">Banca</Label>
                    <Input
                      id="iban_bank"
                      value={settings.iban_bank || ''}
                      onChange={(e) => setSettings({ ...settings, iban_bank: e.target.value })}
                      placeholder="Nome della Banca"
                      data-testid="iban-bank-input"
                    />
                  </div>
                  <p className="text-sm text-gray-600 bg-blue-50 p-3 rounded-lg">
                    <strong>Nota:</strong> Questi dati verranno mostrati ai clienti che scelgono il bonifico bancario come metodo di pagamento.
                  </p>
                </div>
              </div>

              {/* Sezione Email/Resend */}
              <div className="bg-purple-50 p-6 rounded-xl border border-purple-200">
                <h3 className="text-xl font-bold mb-4 text-purple-600">📧 Configurazione Email (Resend)</h3>
                {settings.email_configured && (
                  <div className="mb-4 p-3 bg-green-100 border border-green-300 rounded-lg">
                    <p className="text-green-800 font-semibold">✅ Email configurate correttamente</p>
                    <p className="text-green-700 text-sm">Le notifiche email sono attive.</p>
                  </div>
                )}
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="resend_api_key">Resend API Key</Label>
                    <Input
                      id="resend_api_key"
                      type="password"
                      value={settings.resend_api_key}
                      onChange={(e) => setSettings({ ...settings, resend_api_key: e.target.value })}
                      placeholder={settings.resend_api_key_masked || "Inserisci la API Key di Resend (re_...)"}
                      data-testid="resend-api-key-input"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Ottieni la tua API key gratuita su <a href="https://resend.com" target="_blank" rel="noopener noreferrer" className="text-purple-600 underline">resend.com</a>
                    </p>
                  </div>
                  <div>
                    <Label htmlFor="sender_email">Email Mittente</Label>
                    <Input
                      id="sender_email"
                      type="email"
                      value={settings.sender_email}
                      onChange={(e) => setSettings({ ...settings, sender_email: e.target.value })}
                      placeholder="onboarding@resend.dev (o la tua email verificata)"
                      data-testid="sender-email-input"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Usa <code>onboarding@resend.dev</code> per test, oppure verifica il tuo dominio su Resend.
                    </p>
                  </div>
                </div>
                <p className="text-sm text-gray-600 bg-purple-100 p-3 rounded-lg mt-4">
                  <strong>Funzionalità Email:</strong> Conferma ordine al cliente, notifica ordine pronto, notifica al negozio per nuovi ordini.
                </p>
              </div>

              {/* Sezione Consegna a Domicilio */}
              <div className="bg-orange-50 p-6 rounded-xl border border-orange-200">
                <h3 className="text-xl font-bold mb-4 text-orange-600">🚚 Consegna a Domicilio</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-white rounded-lg border">
                    <div>
                      <p className="font-semibold">Abilita Consegna a Domicilio</p>
                      <p className="text-sm text-gray-600">Permetti ai clienti di scegliere la consegna a casa</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={settings.delivery_enabled}
                        onChange={(e) => setSettings({ ...settings, delivery_enabled: e.target.checked })}
                        className="sr-only peer"
                        data-testid="delivery-enabled-toggle"
                      />
                      <div className="w-14 h-7 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:start-[4px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-orange-500"></div>
                    </label>
                  </div>
                  
                  {settings.delivery_enabled && (
                    <div>
                      <Label htmlFor="delivery_day">Giorno di Consegna</Label>
                      <select
                        id="delivery_day"
                        value={settings.delivery_day}
                        onChange={(e) => setSettings({ ...settings, delivery_day: e.target.value })}
                        className="w-full mt-1 p-3 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                        data-testid="delivery-day-select"
                      >
                        <option value="">Seleziona il giorno...</option>
                        <option value="lunedi">Lunedì</option>
                        <option value="martedi">Martedì</option>
                        <option value="mercoledi">Mercoledì</option>
                        <option value="giovedi">Giovedì</option>
                        <option value="venerdi">Venerdì</option>
                        <option value="sabato">Sabato</option>
                        <option value="domenica">Domenica</option>
                      </select>
                      <p className="text-xs text-gray-500 mt-1">
                        I clienti potranno richiedere la consegna solo in questo giorno della settimana.
                      </p>
                    </div>
                  )}
                </div>
                <p className="text-sm text-gray-600 bg-orange-100 p-3 rounded-lg mt-4">
                  <strong>Nota:</strong> Se disabilitata, i clienti vedranno solo l&apos;opzione &quot;Ritiro in Negozio&quot; durante il checkout.
                </p>
              </div>

              {/* Sezione Testi Homepage */}
              <div className="bg-indigo-50 p-6 rounded-xl border border-indigo-200">
                <h3 className="text-xl font-bold mb-4 text-indigo-600">🏠 Testi Homepage</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Personalizza tutti i testi che appaiono nella homepage del sito.
                </p>
                
                {/* Sezione Hero */}
                <div className="bg-white p-4 rounded-lg mb-4">
                  <h4 className="font-semibold text-indigo-700 mb-3">📌 Sezione Principale (Hero)</h4>
                  <div className="space-y-3">
                    <div>
                      <Label htmlFor="homepage_title">Titolo</Label>
                      <Input
                        id="homepage_title"
                        value={settings.homepage_title}
                        onChange={(e) => setSettings({ ...settings, homepage_title: e.target.value })}
                        placeholder="Civaie"
                      />
                    </div>
                    <div>
                      <Label htmlFor="homepage_subtitle">Sottotitolo</Label>
                      <Input
                        id="homepage_subtitle"
                        value={settings.homepage_subtitle}
                        onChange={(e) => setSettings({ ...settings, homepage_subtitle: e.target.value })}
                        placeholder="Di Lelmi Luciana"
                      />
                    </div>
                    <div>
                      <Label htmlFor="homepage_description">Descrizione</Label>
                      <Textarea
                        id="homepage_description"
                        value={settings.homepage_description}
                        onChange={(e) => setSettings({ ...settings, homepage_description: e.target.value })}
                        placeholder="Scopri la nostra selezione..."
                        rows={2}
                      />
                    </div>
                    <div>
                      <Label htmlFor="homepage_order_text">Testo Ordine</Label>
                      <Input
                        id="homepage_order_text"
                        value={settings.homepage_order_text}
                        onChange={(e) => setSettings({ ...settings, homepage_order_text: e.target.value })}
                        placeholder="Ordina online e scegli consegna o ritiro in negozio."
                      />
                    </div>
                    <div>
                      <Label htmlFor="homepage_button_text">Testo Pulsante</Label>
                      <Input
                        id="homepage_button_text"
                        value={settings.homepage_button_text}
                        onChange={(e) => setSettings({ ...settings, homepage_button_text: e.target.value })}
                        placeholder="Inizia ad Acquistare"
                      />
                    </div>
                  </div>
                </div>

                {/* Sezione Categorie */}
                <div className="bg-white p-4 rounded-lg mb-4">
                  <h4 className="font-semibold text-indigo-700 mb-3">📂 Sezione Categorie</h4>
                  <div>
                    <Label htmlFor="homepage_categories_title">Titolo Sezione</Label>
                    <Input
                      id="homepage_categories_title"
                      value={settings.homepage_categories_title}
                      onChange={(e) => setSettings({ ...settings, homepage_categories_title: e.target.value })}
                      placeholder="Le Nostre Categorie"
                    />
                  </div>
                </div>

                {/* Sezione Features */}
                <div className="bg-white p-4 rounded-lg mb-4">
                  <h4 className="font-semibold text-indigo-700 mb-3">⭐ Sezione Perché Sceglierci</h4>
                  <div className="space-y-3">
                    <div>
                      <Label htmlFor="homepage_features_title">Titolo Sezione</Label>
                      <Input
                        id="homepage_features_title"
                        value={settings.homepage_features_title}
                        onChange={(e) => setSettings({ ...settings, homepage_features_title: e.target.value })}
                        placeholder="Perché Sceglierci"
                      />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div className="space-y-2">
                        <Label>Feature 1</Label>
                        <Input
                          value={settings.homepage_feature1_title}
                          onChange={(e) => setSettings({ ...settings, homepage_feature1_title: e.target.value })}
                          placeholder="Prodotti di Qualità"
                        />
                        <Textarea
                          value={settings.homepage_feature1_desc}
                          onChange={(e) => setSettings({ ...settings, homepage_feature1_desc: e.target.value })}
                          placeholder="Descrizione..."
                          rows={2}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Feature 2</Label>
                        <Input
                          value={settings.homepage_feature2_title}
                          onChange={(e) => setSettings({ ...settings, homepage_feature2_title: e.target.value })}
                          placeholder="Consegna o Ritiro"
                        />
                        <Textarea
                          value={settings.homepage_feature2_desc}
                          onChange={(e) => setSettings({ ...settings, homepage_feature2_desc: e.target.value })}
                          placeholder="Descrizione..."
                          rows={2}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Feature 3</Label>
                        <Input
                          value={settings.homepage_feature3_title}
                          onChange={(e) => setSettings({ ...settings, homepage_feature3_title: e.target.value })}
                          placeholder="Pagamento Sicuro"
                        />
                        <Textarea
                          value={settings.homepage_feature3_desc}
                          onChange={(e) => setSettings({ ...settings, homepage_feature3_desc: e.target.value })}
                          placeholder="Descrizione..."
                          rows={2}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Sezione Footer */}
                <div className="bg-white p-4 rounded-lg">
                  <h4 className="font-semibold text-indigo-700 mb-3">📋 Footer</h4>
                  <div className="space-y-3">
                    <div>
                      <Label htmlFor="footer_text">Testo Footer</Label>
                      <Input
                        id="footer_text"
                        value={settings.footer_text}
                        onChange={(e) => setSettings({ ...settings, footer_text: e.target.value })}
                        placeholder="Civaie - Di Lelmi Luciana"
                      />
                    </div>
                    <div>
                      <Label htmlFor="footer_subtext">Sottotesto Footer</Label>
                      <Input
                        id="footer_subtext"
                        value={settings.footer_subtext}
                        onChange={(e) => setSettings({ ...settings, footer_subtext: e.target.value })}
                        placeholder="40 anni di attività"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <Button
                type="submit"
                className="w-full bg-rose-600 hover:bg-rose-700 text-white rounded-full font-bold py-6"
                data-testid="save-settings-button"
              >
                Salva Impostazioni
              </Button>
            </form>
          </TabsContent>
        </Tabs>
      </div>

      <Dialog open={showProductModal} onOpenChange={setShowProductModal}>
        <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto" data-testid="product-modal">
          <DialogHeader className="sticky top-0 bg-white pb-2 z-10">
            <DialogTitle>{editingProduct ? 'Modifica Prodotto' : 'Aggiungi Prodotto'}</DialogTitle>
          </DialogHeader>

          <form onSubmit={handleProductSubmit} className="space-y-4 pb-4">
            <div>
              <Label htmlFor="name">Nome</Label>
              <Input
                id="name"
                value={productForm.name}
                onChange={(e) => setProductForm({ ...productForm, name: e.target.value })}
                required
                data-testid="product-name-input"
              />
            </div>

            <div>
              <Label htmlFor="description">Descrizione</Label>
              <Textarea
                id="description"
                value={productForm.description}
                onChange={(e) => setProductForm({ ...productForm, description: e.target.value })}
                required
                data-testid="product-description-input"
              />
            </div>

            <div>
              <Label htmlFor="category">Categoria</Label>
              <Select
                value={productForm.category}
                onValueChange={(value) => setProductForm({ ...productForm, category: value })}
              >
                <SelectTrigger data-testid="product-category-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="petshop">Pet Shop</SelectItem>
                  <SelectItem value="giardinaggio">Giardinaggio</SelectItem>
                  <SelectItem value="varie">Varie</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Sottocategoria - Solo per Pet Shop */}
            {productForm.category === 'petshop' && (
              <div>
                <Label htmlFor="subcategory">Sottocategoria</Label>
                <Select
                  value={productForm.subcategory || 'none'}
                  onValueChange={(value) => setProductForm({ ...productForm, subcategory: value === 'none' ? '' : value })}
                >
                  <SelectTrigger data-testid="product-subcategory-select">
                    <SelectValue placeholder="Seleziona sottocategoria" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Nessuna sottocategoria</SelectItem>
                    {getSubcategoryOptions().map((sub) => (
                      <SelectItem 
                        key={sub.value} 
                        value={sub.value}
                        className={sub.isParent ? 'font-semibold' : ''}
                      >
                        {sub.isParent ? `🐾 ${sub.label}` : `  └ ${sub.label.split(' - ')[1]}`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500 mt-1">
                  Seleziona una sottocategoria per Pet Shop (es. Cane - Cibo Secco)
                </p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="price">Prezzo (€)</Label>
                <Input
                  id="price"
                  type="number"
                  step="0.01"
                  value={productForm.price}
                  onChange={(e) => setProductForm({ ...productForm, price: e.target.value })}
                  required
                  data-testid="product-price-input"
                />
              </div>

              <div>
                <Label htmlFor="stock">Stock</Label>
                <Input
                  id="stock"
                  type="number"
                  value={productForm.stock}
                  onChange={(e) => setProductForm({ ...productForm, stock: e.target.value })}
                  required
                  data-testid="product-stock-input"
                />
              </div>
            </div>

            <div>
              <Label>Immagini Prodotto</Label>
              <div className="space-y-3">
                {/* Immagine principale */}
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-sm font-semibold text-gray-700 mb-2">📷 Immagine Principale</p>
                  <div className="flex gap-2 items-center">
                    <Input
                      id="image_file"
                      type="file"
                      accept="image/*"
                      onChange={handleFileUpload}
                      disabled={uploading}
                      className="flex-1 disabled:opacity-50"
                      data-testid="product-image-file"
                    />
                    {uploading && (
                      <div className="flex items-center gap-2 text-sm text-blue-600 font-semibold">
                        <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Caricamento...
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Carica o inserisci URL</p>
                  <Input
                    type="url"
                    placeholder="URL immagine principale"
                    value={productForm.image_url}
                    onChange={(e) => setProductForm({ ...productForm, image_url: e.target.value })}
                    disabled={uploading}
                    className="mt-2 disabled:opacity-50"
                    data-testid="product-image-input"
                  />
                  {productForm.image_url && (
                    <img
                      src={productForm.image_url}
                      alt="Principale"
                      className="w-20 h-20 object-cover rounded-lg border mt-2"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  )}
                </div>

                {/* Immagini aggiuntive */}
                <div className="p-3 bg-blue-50 rounded-lg">
                  <p className="text-sm font-semibold text-blue-700 mb-2">🖼️ Immagini Aggiuntive (Galleria)</p>
                  
                  {/* Lista immagini esistenti */}
                  {productForm.images && productForm.images.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-3">
                      {productForm.images.map((img, index) => (
                        <div key={index} className="relative group">
                          <img
                            src={img}
                            alt={`Immagine ${index + 1}`}
                            className="w-16 h-16 object-cover rounded-lg border"
                            onError={(e) => { e.target.src = 'https://via.placeholder.com/64?text=Err'; }}
                          />
                          <button
                            type="button"
                            onClick={() => {
                              const newImages = productForm.images.filter((_, i) => i !== index);
                              setProductForm({ ...productForm, images: newImages });
                            }}
                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 text-xs flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Aggiungi nuova immagine */}
                  <div className="flex gap-2">
                    <Input
                      type="url"
                      placeholder="Inserisci URL immagine aggiuntiva"
                      value={newImageUrl}
                      onChange={(e) => setNewImageUrl(e.target.value)}
                      className="flex-1"
                      data-testid="additional-image-input"
                    />
                    <Button
                      type="button"
                      onClick={() => {
                        if (newImageUrl.trim()) {
                          setProductForm({
                            ...productForm,
                            images: [...(productForm.images || []), newImageUrl.trim()]
                          });
                          setNewImageUrl('');
                        }
                      }}
                      disabled={!newImageUrl.trim()}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-3"
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Aggiungi più immagini per creare una galleria del prodotto
                  </p>
                </div>
              </div>
            </div>

            <div className="border-t pt-4 mt-4">
              <h3 className="font-semibold text-gray-700 mb-3">Gestione Prodotto Esaurito</h3>
              <p className="text-xs text-gray-500 mb-3">
                Configura cosa mostrare quando il prodotto ha stock = 0
              </p>

              <div className="space-y-4">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="available_on_order"
                    checked={productForm.available_on_order}
                    onCheckedChange={(checked) =>
                      setProductForm({ ...productForm, available_on_order: checked })
                    }
                    data-testid="available-on-order-checkbox"
                  />
                  <Label htmlFor="available_on_order" className="text-sm cursor-pointer">
                    Disponibile su ordinazione
                  </Label>
                </div>

                <div>
                  <Label htmlFor="availability_date">Data Disponibilità Futura (Opzionale)</Label>
                  <Input
                    id="availability_date"
                    type="date"
                    value={productForm.availability_date}
                    onChange={(e) => setProductForm({ ...productForm, availability_date: e.target.value })}
                    data-testid="availability-date-input"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Se impostata, mostrerà &quot;Disponibile dal [data]&quot; quando esaurito
                  </p>
                </div>
              </div>
            </div>

            <div className="sticky bottom-0 bg-white pt-4 mt-4 border-t">
              <Button
                type="submit"
                disabled={uploading}
                className="w-full bg-rose-600 hover:bg-rose-700 text-white rounded-full font-bold py-6 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="save-product-button"
              >
                {uploading ? 'Caricamento immagine...' : editingProduct ? 'Aggiorna Prodotto' : 'Crea Prodotto'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}